import React from 'react';
import { makeStyles } from '@mui/styles';
import InputField from '../../../shared/TextField';
import GenericButton from '../../../shared/Button';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import darkModeLogo from '../../../../../assets/authentication/darkMode_logo.svg';
import lightModeLogo from '../../../../../assets/authentication/lightMode_logo.svg';
import { useNavigate } from 'react-router-dom';
import googleLogo from '../../../../../assets/authentication/logogoogle.svg';
import appleLogo from '../../../../../assets/authentication/ios.svg';
import iosDark from '../../../../../assets/authentication/ios-dark.svg';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useGoogleLogin } from '@react-oauth/google';
import {
  baseURL,
  checkEmail,
  signupWithGoogle,
} from '../../../../../services/api';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { validateGmail } from '../../../../../utils/gmailValidation';

const useStyles = makeStyles((theme) => ({
  Container: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '24px',
    minHeight: '580px',
    padding: '40px',
    maxWidth: '425px',
    [theme.breakpoints.down('md')]: {
      border: 'none',
      width: '100%',
    },
  },
  socialButton: {
    background: 'none !important',
    border: `1px solid ${theme.palette.divider} !important`,
    margin: '10px 0px !important',
    color: `${theme.palette.text.primary} !important`,
  },
  loginText: {
    fontSize: '28px',
    fontWeight: 600,
    color: `${theme.palette.text.primary} !important`,
    marginBottom: '30px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '15px',
  },
  logoText: {
    color: `${theme.palette.text.primary} !important`,
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
  },
  createLink: {
    color: `${theme.palette.primary.main} !important`,
    background: `${theme.palette.background.default} !important`,
  },
  termsAndCondition: {
    color: `${theme.palette.text.primary} !important`,
    fontSize: '14px',
    '& a': {
      color: `${theme.palette.text.primary} !important`,
      fontSize: '14px',
      textDecoration: 'underline',
    },
    marginBottom: '15px',
  },
  alreadyAccount: {
    color: `${theme.palette.text.primary} !important`,
    fontSize: '14px',
    textAlign: 'center',
    '& a': {
      color: `${theme.palette.primary.main} !important`,
      fontSize: '14px',
      // textDecoration: 'underline',
    },
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: '8px',
  },
}));

const SignUpEmail = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const [loadings, setLoadings] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [emailSuggestion, setEmailSuggestion] = React.useState('');
  const [suggestGoogleSignup, setSuggestGoogleSignup] = React.useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validationSchema = Yup.object({
    email: Yup.string()
      .required('Email is required')
      .test('email-validation', (value) => {
        if (!value) return true; // Let required handle empty
        const validation = validateGmail(value);
        if (!validation.isValid) {
          if (validation.suggestion) {
            setEmailSuggestion(validation.suggestion);
          }
          setSuggestGoogleSignup(validation.suggestGoogleSignup || false);
          return false;
        }
        setEmailSuggestion('');
        setSuggestGoogleSignup(false);
        return true;
      }, (value) => {
        const validation = validateGmail(value);
        return validation.errorMessage || 'Enter a valid email address';
      }),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Final validation before submission
      const validation = validateGmail(values.email);
      if (!validation.isValid) {
        setEmailError(validation.errorMessage || 'Enter a valid email address');
        if (validation.suggestion) {
          setEmailSuggestion(validation.suggestion);
        }
        setSuggestGoogleSignup(validation.suggestGoogleSignup || false);
        return;
      }
      
      setLoadings(true);
      setEmailError('');
      setEmailSuggestion('');
      setSuggestGoogleSignup(false);
      await checkEmailIfRegister(values.email);
    },
  });

  // Real-time validation on email change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue('email', value);
    formik.setFieldTouched('email', true, false);
    
    // Clear previous errors
    setEmailError('');
    setEmailSuggestion('');
    setSuggestGoogleSignup(false);
    
    // Only validate if user has typed something
    if (value.trim()) {
      const validation = validateGmail(value);
      if (!validation.isValid) {
        setEmailError(validation.errorMessage || 'Enter a valid email address');
        if (validation.suggestion) {
          setEmailSuggestion(validation.suggestion);
        }
        setSuggestGoogleSignup(validation.suggestGoogleSignup || false);
      }
    }
  };

  const checkEmailIfRegister = async (emailid) => {
    // Final email validation before proceeding
    const validation = validateGmail(emailid);
    if (!validation.isValid) {
      setEmailError(validation.errorMessage || 'Enter a valid email address');
      if (validation.suggestion) {
        setEmailSuggestion(validation.suggestion);
      }
      setSuggestGoogleSignup(validation.suggestGoogleSignup || false);
      setLoadings(false);
      return;
    }
    
    const res = await checkEmail(String(emailid).toLowerCase());
    console.log(res);
    if (res.status === 200 && !res.success) {
      setErrorMessage('This account already exists. please log in.');
      setLoadings(false);
      return;
    }
    setLoadings(false);
    console.log('res', res.status);
    
    // Store email in session for flow tracking
    sessionStorage.setItem('signupEmail', emailid);
    sessionStorage.removeItem('emailVerified'); // Reset verification status
    
    navigate('/auth/signup-email-verification', {
      state: { email: emailid },
    });
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    console.log('Google signup tokenResponse received:', tokenResponse);

    try {
      // Generate reCAPTCHA token - REQUIRED, no bypass allowed
      let recaptchaToken = '';
      console.log('🔍 Checking executeRecaptcha availability:', typeof executeRecaptcha, executeRecaptcha ? 'Available' : 'Not available');
      
      if (executeRecaptcha) {
        try {
          console.log('🔄 Generating reCAPTCHA token for Google signup...');
          recaptchaToken = await executeRecaptcha('signup');
          console.log('✅ reCAPTCHA token generated:', recaptchaToken ? 'Token received' : 'No token');
          if (recaptchaToken) {
            console.log('📝 Token preview:', recaptchaToken.substring(0, 50) + '...');
            console.log('📝 Full token length:', recaptchaToken.length);
          } else {
            console.warn('⚠️ reCAPTCHA token is empty');
            setErrorMessage('reCAPTCHA verification is required. Please refresh the page and try again.');
            return;
          }
        } catch (error) {
          console.error('❌ reCAPTCHA error:', error);
          console.error('❌ Error details:', error.message, error.stack);
          setErrorMessage('reCAPTCHA verification failed. Please refresh the page and try again.');
          return;
        }
      } else {
        console.warn('⚠️ executeRecaptcha is not available. reCAPTCHA may not be loaded.');
        console.warn('⚠️ This might be because the component is not wrapped in GoogleReCaptchaProvider or the script hasn\'t loaded yet.');
        setErrorMessage('reCAPTCHA is not available. Please refresh the page and try again.');
        return;
      }

      if (!recaptchaToken) {
        setErrorMessage('reCAPTCHA verification is required. Please try again.');
        return;
      }

      console.log(
        'Sending Google token to backend for signup:',
        tokenResponse?.access_token
      );
      console.log('📤 Sending reCAPTCHA token to backend:', recaptchaToken ? `Token (${recaptchaToken.length} chars)` : 'No token');

      const res = await signupWithGoogle(tokenResponse?.access_token, recaptchaToken);
      console.log('Backend response for Google signup:', res);

      if (res.status === 200) {
        console.log('Google signup successful, redirecting to login');
        window.location.href = `${baseURL}/auth/login?redirectWebsiteLink=exchange`;
      } else {
        console.error('Google signup failed with response:', res);
        // Handle rate limit errors with user-friendly message
        const errorMessage = res.isRateLimit ? res.data : res.data || 'Signup failed. Please try again.';
        setErrorMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error during Google signup:', error);
      setErrorMessage('Signup error. Please try again later.');
    }
  };

  const login = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: (error) => {
      console.error('Google signup error:', error);
      setErrorMessage('Signup Failed: ' + (error.message || 'Unknown error'));
    },
  });

  return (
    <div className={classes.Container}>
      <div className={classes.logoContainer}>
        <img
          src={theme.palette.mode === 'light' ? lightModeLogo : darkModeLogo}
          style={{ width: '40px' }}
        />
        <h2 className={classes.logoText}>Indexx Exchange</h2>
      </div>

      <h3 className={classes.loginText}>Welcome to Indexx Exchange</h3>
      <div style={{ margin: '15px auto' }}>
        <InputField
          label={'Email'}
          type="text"
          {...formik.getFieldProps('email')}
          onChange={handleEmailChange}
          error={(formik.touched.email && (formik.errors.email || emailError)) ? true : false}
          helperText={emailError || emailSuggestion || formik.errors.email}
        />
        {emailSuggestion && (
          <p 
            style={{ 
              color: theme.palette.primary.main, 
              fontSize: '12px', 
              marginTop: '4px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => {
              formik.setFieldValue('email', emailSuggestion);
              setEmailSuggestion('');
              setEmailError('');
              setSuggestGoogleSignup(false);
            }}
          >
            Click to use: {emailSuggestion}
          </p>
        )}
        {suggestGoogleSignup && !emailSuggestion && (
          <p 
            style={{ 
              color: theme.palette.primary.main, 
              fontSize: '12px', 
              marginTop: '4px',
              fontStyle: 'italic'
            }}
          >
            💡 Tip: Use "Continue with Google" button below to sign up with any email address
          </p>
        )}
        {errorMessage && <p className={classes.errorText}>{errorMessage}</p>}
      </div>
      <p className={classes.termsAndCondition}>
        By creating an account, I agree to Indexx's{' '}
        <Link>Terms of Service</Link> and <Link>Privacy Policy.</Link>
      </p>

      <GenericButton
        text={'Next'}
        onClick={formik.handleSubmit}
        loading={loadings}
      />
      <div style={{ margin: '10px auto' }}></div>
      <Divider>or</Divider>
      <GenericButton
        text={'Continue with Google'}
        className={classes.socialButton}
        IconComponent={
          <img
            src={googleLogo}
            style={{ width: '100%', height: '100%', marginTop: '-8px' }}
          />
        }
        onClick={() => login()}
      />
      <GenericButton
        text={'Continue with Phone'}
        className={classes.socialButton}
        IconComponent={
          <PhoneAndroidIcon
            style={{
              marginTop: '-10px',
            }}
          />
        }
        onClick={() => navigate('/auth/signup-phone')}
      />
      <div style={{ margin: '20px auto' }}></div>

      <p className={classes.alreadyAccount}>
        Already have an account? <Link to="/auth/login">Login</Link>
      </p>
    </div>
  );
};

export default SignUpEmail;
